<template>
    <b-container id="copy-attachments-page" fluid>
        <b-row>
            <b-col cols="12">
                <div class="border-bottom mb-3 pb-3">
                    <h3>이전 캠페인 첨부파일 복사</h3>
                    <p class="fw-400 text-secondary mb-0">
                        프로젝트를 선택하여, 해당 프로젝트의 이전 캠페인 첨부파일을 현재 캠페인으로 복사합니다.
                        복사 대상은 기업별 sub_code 매핑이 일치하는 첨부파일이며, 실행 전 미리보기로 목록을 확인할 수 있습니다.
                    </p>
                </div>
            </b-col>
        </b-row>

        <b-row class="mb-3">
            <b-col cols="12">
                <b-card no-body bg-variant="white">
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <h5 class="mb-0">프로젝트 선택</h5>
                        </div>
                    </template>
                    <div class="p-3">
                        <b-alert variant="info" show class="mb-3">
                            <strong>안내:</strong> 현재 접속한 템플릿을 기준으로 동일한 템플릿 코드를 사용하는 프로젝트의 이전 캠페인 첨부파일이 복사됩니다.
                        </b-alert>
                        <b-form @submit.stop.prevent="onPreview">
                            <b-row>
                                <b-col cols="12" md="6" lg="4">
                                    <b-form-group
                                        label="프로젝트"
                                        label-for="input-project-code"
                                    >
                                        <auto-complete-select
                                            id="input-project-code"
                                            ref="projectCodeAutoComplete"
                                            v-model="selectedProjectId"
                                            :options="projectCodeOptions"
                                            :loading="projectsLoading"
                                            start-expanded
                                            list-max-height="360px"
                                            option-value-key="id"
                                            placeholder="프로젝트를 선택하세요"
                                            :disabled="isBusy"
                                            clearable
                                            @input="onProjectSelected"
                                        />
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <div class="d-flex align-items-center">
                                <b-button
                                    type="submit"
                                    variant="outline-primary"
                                    class="mr-2"
                                    :disabled="!projectCode || isBusy"
                                >
                                    <b-spinner v-if="previewLoading" small class="mr-1"/>
                                    미리보기
                                </b-button>
                                <b-button
                                    variant="primary"
                                    :disabled="!projectCode || isBusy || projectHasActiveTask"
                                    @click="onExecute"
                                >
                                    <b-spinner v-if="executing" small class="mr-1"/>
                                    실행
                                </b-button>
                                <small v-if="projectHasActiveTask" class="text-muted ml-2">이 프로젝트의 복사가 진행 중입니다</small>
                                <b-button
                                    v-if="previewResult || projectCode"
                                    variant="link"
                                    class="ml-2"
                                    :disabled="isBusy"
                                    @click="resetAll"
                                >
                                    초기화
                                </b-button>
                            </div>
                        </b-form>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <b-row v-if="previewResult" class="mb-3">
            <b-col cols="12">
                <b-card no-body bg-variant="white">
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <h5 class="mb-0">미리보기 결과 — {{ previewProjectCode }}</h5>
                            <b-button
                                v-if="allPreviewRows.length > 0"
                                variant="outline-success"
                                size="sm"
                                @click="downloadPreviewCsv"
                            >
                                CSV 다운로드
                            </b-button>
                        </div>
                    </template>
                    <div class="p-3">
                        <b-row class="mb-3">
                            <b-col cols="6" md="3">
                                <div class="summary-box">
                                    <p class="small text-secondary mb-1">현재 캠페인 ID</p>
                                    <p class="h5 mb-0">{{ previewResult.campaignId || '-' }}</p>
                                </div>
                            </b-col>
                            <b-col cols="6" md="3">
                                <div class="summary-box">
                                    <p class="small text-secondary mb-1">이전 캠페인 ID</p>
                                    <p class="h5 mb-0">{{ previewPreviousCampaignId }}</p>
                                </div>
                            </b-col>
                            <b-col cols="6" md="3">
                                <div class="summary-box">
                                    <p class="small text-secondary mb-1">대상 기업 수</p>
                                    <p class="h5 mb-0">{{ previewCompanyCount }}</p>
                                </div>
                            </b-col>
                            <b-col cols="6" md="3">
                                <div class="summary-box">
                                    <p class="small text-secondary mb-1">복사 예상</p>
                                    <p class="h5 mb-0 text-success">{{ previewCopyCount }}</p>
                                </div>
                            </b-col>
                            <b-col v-if="previewSkipCount > 0" cols="6" md="3">
                                <div class="summary-box">
                                    <p class="small text-secondary mb-1">스킵 예상</p>
                                    <p class="h5 mb-0 text-warning">{{ previewSkipCount }}</p>
                                </div>
                            </b-col>
                        </b-row>

                        <div v-if="previewTotalRows > 0">
                            <h6 class="mb-2">
                                전체 {{ previewTotalRows }}건
                                <small class="text-secondary">(복사 {{ previewCopyCount }} / 스킵 {{ previewSkipCount }})</small>
                            </h6>
                            <b-table
                                id="copy-attachments-preview-table"
                                :items="previewRows"
                                :fields="previewFields"
                                small
                                striped
                                responsive
                                class="mb-0"
                            >
                                <template #cell(action)="{ item }">
                                    <b-badge :variant="item.action === 'copy' ? 'success' : 'warning'">
                                        {{ item.action === 'copy' ? '복사' : '스킵' }}
                                    </b-badge>
                                </template>
                                <template #cell(reason)="{ item }">
                                    <small v-if="item.reason" class="text-secondary">{{ item.reason }}</small>
                                    <span v-else>-</span>
                                </template>
                            </b-table>
                            <div class="d-flex justify-content-between align-items-center flex-wrap mt-3">
                                <p class="small text-secondary mb-2 mb-md-0">
                                    총 {{ previewTotalRows }}건 중 {{ previewPageStart }}-{{ previewPageEnd }}건 표시
                                </p>
                                <div class="d-flex align-items-center flex-wrap justify-content-end">
                                    <b-pagination
                                        v-model="previewPagination.currentPage"
                                        aria-controls="copy-attachments-preview-table"
                                        :total-rows="previewTotalRows"
                                        :per-page="previewPagination.perPage"
                                        first-number
                                        last-number
                                        align="right"
                                        size="sm"
                                        class="mb-0"
                                    />
                                    <b-form-select
                                        v-model="previewPagination.perPage"
                                        class="ml-3"
                                        style="width: 100px"
                                        size="sm"
                                        @change="handlePreviewPerPageChange"
                                    >
                                        <b-form-select-option
                                            v-for="option in perPageOptions"
                                            :key="`${option.text}-${option.value}`"
                                            :value="option.value"
                                        >
                                            {{ option.text }}
                                        </b-form-select-option>
                                    </b-form-select>
                                </div>
                            </div>
                        </div>
                        <p v-else class="text-secondary mb-0">복사할 파일이 없습니다.</p>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <b-row v-if="trackedTasks.length > 0" class="mb-3">
            <b-col cols="12">
                <b-card no-body bg-variant="white">
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <h5 class="mb-0">실행 현황</h5>
                            <b-badge v-if="activeTaskCount > 0" variant="info">
                                {{ activeTaskCount }}건 진행 중
                            </b-badge>
                        </div>
                    </template>
                    <div class="p-3">
                    <b-table
                        :items="trackedTasks"
                        :fields="trackedTaskFields"
                        small
                        striped
                        responsive
                        class="mb-0"
                    >
                        <template #cell(status)="{ item }">
                            <b-badge :variant="getTaskStatusVariant(item.status)">{{ item.status }}</b-badge>
                            <b-badge v-if="item.checkFailed" variant="warning" class="ml-1">상태 확인 실패</b-badge>
                        </template>
                        <template #cell(progress)="{ item }">
                            <div v-if="item.status === 'FAILURE' || item.status === 'REVOKED'">
                                <small class="text-danger">{{ item.errorMessage || '실행 실패' }}</small>
                            </div>
                            <div v-else-if="item.progress && item.progress.total > 0" style="min-width: 120px;">
                                <b-progress :max="item.progress.total" height="8px" :animated="!isTerminalStatus(item.status)">
                                    <b-progress-bar :value="item.progress.current" :variant="getTaskStatusVariant(item.status)"/>
                                </b-progress>
                                <small class="text-secondary">{{ item.progress.current }} / {{ item.progress.total }}</small>
                            </div>
                            <small v-else class="text-secondary">대기 중</small>
                        </template>
                        <template #cell(startedAt)="{ item }">
                            <small class="text-secondary">{{ formatTaskTime(item.startedAt) }}</small>
                        </template>
                        <template #cell(actions)="{ item }">
                            <b-button
                                v-if="item.status === 'SUCCESS'"
                                variant="link"
                                size="sm"
                                class="p-0 mr-2"
                                @click="showResult(item.taskId)"
                            >
                                결과 보기
                            </b-button>
                            <b-button
                                v-if="item.checkFailed"
                                variant="link"
                                size="sm"
                                class="p-0 mr-2"
                                @click="retryPolling(item.taskId)"
                            >
                                재시도
                            </b-button>
                            <b-button
                                v-if="isTerminalStatus(item.status) || item.checkFailed"
                                variant="link"
                                size="sm"
                                class="p-0 text-secondary"
                                @click="dismissTrackedTask(item.taskId)"
                            >
                                닫기
                            </b-button>
                            <b-button
                                v-else
                                variant="link"
                                size="sm"
                                class="p-0 text-secondary"
                                @click="stopTracking(item)"
                            >
                                추적 중지
                            </b-button>
                        </template>
                    </b-table>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <b-row v-if="finalResult" class="mb-3">
            <b-col cols="12">
                <b-card no-body bg-variant="white">
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <h5 class="mb-0">실행 결과<template v-if="finalResult.projectCode"> — {{ finalResult.projectCode }}</template></h5>
                            <b-button
                                variant="outline-success"
                                size="sm"
                                :disabled="csvDownloading"
                                @click="downloadResultCsv"
                            >
                                <b-spinner v-if="csvDownloading" small class="mr-1"/>
                                CSV 다운로드
                            </b-button>
                        </div>
                    </template>
                    <div class="p-3">
                        <b-row class="mb-3">
                            <b-col cols="6" md="3">
                                <div class="summary-box">
                                    <p class="small text-secondary mb-1">복사 완료</p>
                                    <p class="h5 mb-0 text-success">{{ finalCopiedCount }}</p>
                                </div>
                            </b-col>
                            <b-col cols="6" md="3">
                                <div class="summary-box">
                                    <p class="small text-secondary mb-1">스킵</p>
                                    <p class="h5 mb-0 text-warning">{{ finalSkippedCount }}</p>
                                </div>
                            </b-col>
                            <b-col cols="6" md="3">
                                <div class="summary-box">
                                    <p class="small text-secondary mb-1">대상 기업</p>
                                    <p class="h5 mb-0">{{ finalCompanyCount }}</p>
                                </div>
                            </b-col>
                            <b-col cols="6" md="3">
                                <div class="summary-box">
                                    <p class="small text-secondary mb-1">이전 캠페인 ID</p>
                                    <p class="h5 mb-0">{{ finalPreviousCampaignId }}</p>
                                </div>
                            </b-col>
                        </b-row>

                        <div v-if="edgeCases.length > 0" class="mt-3">
                            <h6 class="mb-2">엣지 케이스 ({{ edgeCases.length }}건)</h6>
                            <b-table
                                :items="edgeCases"
                                :fields="edgeCaseFields"
                                small
                                striped
                                responsive
                                class="mb-0"
                            />
                        </div>
                    </div>
                </b-card>
            </b-col>
        </b-row>
    </b-container>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import AutoCompleteSelect from '@core/components/common/AutoCompleteSelect.vue';
import { getDefaultPagination, getPerPageOptions, getProjectCodeOptions } from '../common-data';

const POLL_INTERVAL_MS = 2000;
const SNAKE_CASE_REGEX = /_([a-z])/g;
const STORAGE_KEY = 'copy-attachments-tracked-tasks';
const TERMINAL_STATUSES = ['SUCCESS', 'FAILURE', 'REVOKED'];
// 끝난 작업은 닫지 않아도 이 기간이 지나면 실행 현황에서 버린다
const TASK_KEEP_MS = 7 * 24 * 60 * 60 * 1000;

// vue-router 는 같은 키가 여러 번이면 배열을 준다 — 문자열 하나만 값으로 본다
function queryProjectId(value) {
    const n = typeof value === 'string' && value !== '' ? Number(value) : NaN;
    return Number.isInteger(n) && n > 0 ? n : null;
}

function queryTaskId(value) {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function sameQuery(a, b) {
    const keys = Object.keys(a);
    return keys.length === Object.keys(b).length && keys.every(k => String(a[k]) === String(b[k]));
}

function isPlainObject(value) {
    return Object.prototype.toString.call(value) === '[object Object]';
}

function toCamelCaseKey(key) {
    return key.replace(SNAKE_CASE_REGEX, (_, char) => char.toUpperCase());
}

function normalizeCopyAttachmentsResponse(value) {
    if (Array.isArray(value)) {
        return value.map(normalizeCopyAttachmentsResponse);
    }
    if (!isPlainObject(value)) {
        return value;
    }

    return Object.entries(value).reduce((acc, [key, itemValue]) => {
        acc[toCamelCaseKey(key)] = normalizeCopyAttachmentsResponse(itemValue);
        return acc;
    }, {});
}

export default {
    name: 'CopyAttachments',
    components: {
        AutoCompleteSelect,
    },
    data() {
        return {
            projects: null,
            projectsLoading: false,
            selectedProjectId: null,
            projectCode: '',
            previewLoading: false,
            executing: false,
            csvDownloading: false,
            trackedTasks: [],
            pollFails: {},   // taskId → 연속 상태 조회 실패 횟수
            previewPagination: getDefaultPagination(1, 20),
            perPageOptions: getPerPageOptions(),

            previewResult: null,
            previewProjectCode: '',   // previewResult 가 어느 프로젝트 것인지
            // 늦게 온 응답을 버리기 위한 요청 순번
            previewSeq: 0,
            resultSeq: 0,

            pollInterval: null,
            pollBusy: false,

            finalResult: null,
            resultTaskId: null,   // finalResult(또는 조회 중인 결과)가 어느 작업 것인지 — URL task_id 와 같다

            previewFields: [
                { key: 'companyName', label: '기업명' },
                { key: 'action', label: '동작' },
                { key: 'oldSubCode', label: '이전 sub_code' },
                { key: 'newSubCode', label: '현재 sub_code' },
                { key: 'filename', label: '파일명' },
                { key: 'reason', label: '사유' },
            ],
            edgeCaseFields: [
                { key: 'companyId', label: '기업 ID' },
                { key: 'companyName', label: '기업명' },
                { key: 'oldSubCode', label: '이전 sub_code' },
                { key: 'newSubCode', label: '현재 sub_code' },
                { key: 'reason', label: '사유' },
                { key: 'filename', label: '파일명' },
            ],
            trackedTaskFields: [
                { key: 'projectCode', label: '프로젝트' },
                { key: 'status', label: '상태' },
                { key: 'progress', label: '진행률' },
                { key: 'startedAt', label: '시작' },
                { key: 'actions', label: '', thStyle: 'width: 60px' },
            ],
        };
    },
    computed: {
        projectCodeOptions() {
            return getProjectCodeOptions(this.projects);
        },
        isBusy() {
            return this.previewLoading || this.executing || this.csvDownloading;
        },
        projectHasActiveTask() {
            return this.trackedTasks.some(t => t.projectCode === this.projectCode && !TERMINAL_STATUSES.includes(t.status));
        },
        activeTaskCount() {
            return this.trackedTasks.filter(t => !TERMINAL_STATUSES.includes(t.status)).length;
        },
        isPolling() {
            return this.pollInterval !== null;
        },
        allPreviewRows() {
            if (!this.previewResult) return [];
            return this.previewResult.plannedRows || this.previewResult.previewRows || [];
        },
        previewRows() {
            const start = (this.previewPagination.currentPage - 1) * this.previewPagination.perPage;
            const end = start + this.previewPagination.perPage;
            return this.allPreviewRows.slice(start, end);
        },
        previewTotalRows() {
            return this.allPreviewRows.length || this.previewPlannedCount || 0;
        },
        previewPageStart() {
            if (this.previewTotalRows === 0) return 0;
            return ((this.previewPagination.currentPage - 1) * this.previewPagination.perPage) + 1;
        },
        previewPageEnd() {
            if (this.previewTotalRows === 0) return 0;
            return Math.min(this.previewPagination.currentPage * this.previewPagination.perPage, this.previewTotalRows);
        },
        previewPreviousCampaignId() {
            return this.previewResult?.previousCampaignId || '-';
        },
        previewCompanyCount() {
            return this.previewResult?.companyCount || this.previewResult?.totalCompanies || 0;
        },
        previewPlannedCount() {
            return this.previewResult?.plannedCount || this.previewResult?.totalAttachments || 0;
        },
        previewCopyCount() {
            const rows = this.allPreviewRows;
            if (!rows.length) return this.previewPlannedCount;
            return rows.filter(r => r.action === 'copy').length;
        },
        previewSkipCount() {
            return this.allPreviewRows.filter(r => r.action === 'skip_duplicate').length;
        },
        edgeCases() {
            return this.finalResult?.edgeCases || [];
        },
        finalCopiedCount() {
            return this.finalResult?.copiedCount || 0;
        },
        finalSkippedCount() {
            return this.finalResult?.skippedCount || 0;
        },
        finalCompanyCount() {
            return this.finalResult?.companyCount || this.finalResult?.totalCompanies || 0;
        },
        finalPreviousCampaignId() {
            return this.finalResult?.previousCampaignId || '-';
        },
    },
    watch: {
        // 같은 화면 안의 query 변화(뒤로가기·사이드바 재클릭·링크)
        '$route.query'() {
            if (!this.left) this.applyQuery();
        },
    },
    async created() {
        // 다른 탭에서 실행·닫은 작업을 이 탭의 실행 현황에도 반영한다
        window.addEventListener('storage', this.onStorage);
        await this.fetchProjects();
        if (this.left) return;
        this.loadTrackedTasks();
        this.applyQuery();
        if (this.activeTaskCount > 0) {
            this.startPolling();
        }
    },
    beforeDestroy() {
        // 떠난 뒤 늦게 끝난 요청이 다른 화면의 URL 을 고치지 않게 한다
        this.left = true;
        this.stopPolling();
        window.removeEventListener('storage', this.onStorage);
    },
    methods: {
        async fetchProjects() {
            this.projectsLoading = true;
            try {
                const response = await this.$axios.get('/project_code');
                this.projects = response.data;
            } catch (error) {
                console.error('Failed to load projects:', error);
                this.$EventBus.$emit('toast-failure', '프로젝트 로드 실패', '프로젝트 목록을 불러오는 중 오류가 발생했습니다.');
            } finally {
                this.projectsLoading = false;
            }
        },
        // ── URL: ?project_id=(선택 프로젝트)&task_id=(보고 있는 실행 결과). 미리보기는 URL 에 싣지 않는다 ──
        /** URL → 화면 (첫 진입·뒤로가기·링크). 미리보기는 자동으로 실행하지 않는다. */
        applyQuery() {
            const q = this.$route.query;
            const fix = {};
            const projectId = queryProjectId(q.project_id);
            // 빈 값·잘못된 값(?project_id=&task_id=)은 지운다
            if ('project_id' in q && !projectId) fix.project_id = undefined;
            // 프로젝트 목록을 못 받았으면 키를 그대로 둔다 (없는 프로젝트와 구분)
            if (projectId !== this.selectedProjectId && this.projects) {
                if (projectId && !this.findProjectCode(projectId)) {
                    this.$EventBus.$emit('toast-warning', '프로젝트 없음', '해당 프로젝트를 찾을 수 없습니다.');
                    this.selectProject(null);
                    fix.project_id = undefined;
                } else {
                    this.selectProject(projectId);
                }
            }
            const taskId = queryTaskId(q.task_id);
            if ('task_id' in q && !taskId) fix.task_id = undefined;
            if (Object.keys(fix).length) this.setQuery(fix, 'replace');
            if (taskId !== this.resultTaskId) {
                if (taskId) this.loadResult(taskId, null);
                else this.clearResult();
            }
        },
        /** 화면 → URL. undefined 값은 키를 지운다. 사용자 조작은 push, 자동 보정은 replace. */
        setQuery(patch, mode) {
            if (this.left) return;
            const query = {...this.$route.query, ...patch};
            Object.keys(query).forEach(key => {
                if (query[key] === undefined) delete query[key];
            });
            if (sameQuery(query, this.$route.query)) return;
            this.$router[mode]({query}).catch(() => {});
        },
        findProjectCode(projectId) {
            for (const group of this.projectCodeOptions) {
                const item = group.items.find(i => i.id === projectId);
                if (item) return item.value.project_code;
            }
            return '';
        },
        /** 프로젝트가 실제로 바뀌면 그 전 프로젝트의 미리보기를 지운다 (도착 전인 미리보기 응답도 버림). 바뀌었으면 true. */
        selectProject(projectId) {
            this.selectedProjectId = projectId;
            const code = projectId ? this.findProjectCode(projectId) : '';
            if (code === this.projectCode) return false;
            this.projectCode = code;
            this.previewSeq++;
            this.previewResult = null;
            this.previewProjectCode = '';
            this.resetPreviewPagination();
            return true;
        },
        // 위젯에서 고르거나 지움 (push). 다른 프로젝트로 바꾸면 이전 실행 결과 카드도 닫는다
        onProjectSelected(projectId) {
            if (this.selectProject(projectId || null)) this.clearResult();
            this.setQuery({
                project_id: projectId ? String(projectId) : undefined,
                task_id: this.resultTaskId || undefined,
            }, 'push');
        },
        async onPreview() {
            // 요청 시점의 프로젝트로 기록한다 (응답 전에 선택이 바뀌면 그 응답은 버린다)
            const projectCode = this.projectCode;
            if (!projectCode) return;

            const seq = ++this.previewSeq;
            this.previewLoading = true;
            this.previewResult = null;
            this.resetPreviewPagination();

            try {
                const res = await this.$axios.post('/admin/copy-attachments/preview', {
                    project_code: projectCode,
                });
                if (seq !== this.previewSeq) return;

                if (res.data?.result === 'ok' || res.data?.data) {
                    this.previewResult = normalizeCopyAttachmentsResponse(res.data.data);
                    this.previewProjectCode = projectCode;
                    this.syncPreviewPagination();
                    this.$EventBus.$emit('toast-success', '미리보기 완료', `복사 예상 파일 수: ${this.previewPlannedCount}`);
                } else {
                    this.$EventBus.$emit('toast-failure', '미리보기 실패', res.data?.message || '응답을 해석할 수 없습니다.');
                }
            } catch (error) {
                if (seq !== this.previewSeq || error?.response?.status === 401) return;
                console.error('Preview error:', error);
                const message = getErrorMessage(error, '') || error.message || '알 수 없는 오류가 발생했습니다.';
                this.$EventBus.$emit('toast-failure', '미리보기 실패', message);
            } finally {
                this.previewLoading = false;
            }
        },
        handlePreviewPerPageChange(value) {
            this.previewPagination.perPage = Number(value) || this.previewPagination.perPage;
            this.previewPagination.currentPage = 1;
            this.syncPreviewPagination();
        },
        resetPreviewPagination() {
            this.previewPagination.currentPage = 1;
            this.previewPagination.totalRows = 0;
        },
        syncPreviewPagination() {
            this.previewPagination.totalRows = this.previewTotalRows;
            const totalPages = Math.max(1, Math.ceil(this.previewPagination.totalRows / this.previewPagination.perPage));
            if (this.previewPagination.currentPage > totalPages) {
                this.previewPagination.currentPage = totalPages;
            }
        },

        async onExecute() {
            // 확인창이 떠 있는 동안 선택이 바뀌어도(뒤로가기 등) 확인한 프로젝트로만 실행한다
            const projectCode = this.projectCode;
            if (!projectCode) return;

            const hasPreview = this.previewResult && this.previewProjectCode === projectCode;
            const confirmed = await this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: '실행 확인',
                message: `프로젝트 [${projectCode}]의 이전 캠페인 첨부파일을 복사합니다. `
                    + (hasPreview
                        ? `복사 예정 ${this.previewCopyCount}건, 스킵 ${this.previewSkipCount}건, 대상 업체 ${this.previewCompanyCount}개입니다. `
                        : '미리보기를 먼저 실행하면 예상 건수를 볼 수 있습니다. ')
                    + '계속하시겠습니까?',
            });
            if (!confirmed) return;
            if (this.projectCode !== projectCode) {
                this.$EventBus.$emit('toast-warning', '실행 취소', '프로젝트가 바뀌어 실행하지 않았습니다. 다시 실행해 주세요.');
                return;
            }

            this.executing = true;
            this.clearResult();
            this.setQuery({task_id: undefined}, 'replace');

            try {
                const res = await this.$axios.post('/admin/copy-attachments/execute', {
                    project_code: projectCode,
                });

                const data = normalizeCopyAttachmentsResponse(res.data?.data);
                if (!data?.taskId) {
                    throw new Error(res.data?.message || '태스크 ID를 받지 못했습니다.');
                }

                // 서버가 진행 중인 태스크의 id 를 그대로 돌려주면 한 줄로만 추적한다.
                if (!this.trackedTasks.some(t => t.taskId === data.taskId)) {
                    this.trackedTasks.push({
                        taskId: data.taskId,
                        projectCode,
                        status: data.status || 'PENDING',
                        progress: null,
                        errorMessage: null,
                        startedAt: new Date().toISOString(),
                    });
                    this.saveTrackedTasks();
                }

                this.$EventBus.$emit('toast-info', '실행 시작', data.message || '첨부파일 복사 태스크가 시작되었습니다.');

                this.startPolling();
            } catch (error) {
                if (error?.response?.status === 401) return;
                console.error('Execute error:', error);
                const message = getErrorMessage(error, '') || error.message || '알 수 없는 오류가 발생했습니다.';
                this.$EventBus.$emit('toast-failure', '실행 실패', message);
            } finally {
                this.executing = false;
            }
        },

        startPolling() {
            this.stopPolling();
            this.fetchStatus();
            this.pollInterval = setInterval(() => {
                this.fetchStatus();
            }, POLL_INTERVAL_MS);
        },

        stopPolling() {
            if (this.pollInterval) {
                clearInterval(this.pollInterval);
                this.pollInterval = null;
            }
        },

        async fetchStatus() {
            // 한 바퀴가 2초를 넘기면 다음 틱은 건너뛴다 (같은 작업을 겹쳐 조회해 결과를 두 번 띄우지 않게)
            if (this.pollBusy) return;
            const activeTasks = this.trackedTasks.filter(t => !TERMINAL_STATUSES.includes(t.status) && !t.checkFailed);
            if (activeTasks.length === 0) {
                this.stopPolling();
                return;
            }
            this.pollBusy = true;
            try {
                for (const task of activeTasks) {
                    await this.pollSingleTask(task);
                }
            } finally {
                this.pollBusy = false;
            }
        },

        async pollSingleTask(task) {
            try {
                const res = await this.$axios.get(`/admin/copy-attachments/status/${task.taskId}`);
                const data = normalizeCopyAttachmentsResponse(res.data?.data);
                if (!data) return;
                this.pollFails[task.taskId] = 0;

                const idx = this.trackedTasks.findIndex(t => t.taskId === task.taskId);
                if (idx === -1) return;

                this.$set(this.trackedTasks, idx, {
                    ...this.trackedTasks[idx],
                    status: data.status || task.status,
                    progress: data.progress || task.progress,
                    errorMessage: data.errorMessage || null,
                });

                if (data.status === 'SUCCESS') {
                    // 완료 작업은 목록에 남겨 결과 보기/닫기를 제공한다(결과 조회 실패 시에도 다시 볼 수 있게)
                    this.saveTrackedTasks();
                    // 지금 보고 있는 프로젝트의 작업이면 결과를 바로 띄운다(replace). 다른 프로젝트를 보는 중이면 알리기만 한다
                    if (!this.projectCode || task.projectCode === this.projectCode) {
                        await this.loadResult(task.taskId, 'replace');
                    } else {
                        this.$EventBus.$emit('toast-success', '실행 완료', `[${task.projectCode}] 복사가 끝났습니다. 실행 현황의 '결과 보기'로 확인하세요.`);
                    }
                } else if (data.status === 'FAILURE' || data.status === 'REVOKED') {
                    this.saveTrackedTasks();
                    this.$EventBus.$emit('toast-failure', '실행 실패', `[${task.projectCode}] ${data.errorMessage || '태스크 실행에 실패했습니다.'}`);
                }
            } catch (error) {
                console.error(`Status poll error for ${task.taskId}:`, error);
                // 일시 실패는 다음 틱에서 재시도, 연속 3회 실패하면 중단하고 표시한다
                this.pollFails[task.taskId] = (this.pollFails[task.taskId] || 0) + 1;
                if (this.pollFails[task.taskId] >= 3) {
                    const idx = this.trackedTasks.findIndex(t => t.taskId === task.taskId);
                    if (idx !== -1) this.$set(this.trackedTasks, idx, {...this.trackedTasks[idx], checkFailed: true});
                    this.$EventBus.$emit('toast-failure', '상태 확인 실패', `[${task.projectCode}] 작업 상태를 확인하지 못했습니다. [재시도] 또는 [닫기]를 눌러주세요.`);
                }
            }
        },
        retryPolling(taskId) {
            const idx = this.trackedTasks.findIndex(t => t.taskId === taskId);
            if (idx === -1) return;
            this.pollFails[taskId] = 0;
            this.$set(this.trackedTasks, idx, {...this.trackedTasks[idx], checkFailed: false});
            this.startPolling();
        },
        // 실행 현황의 '결과 보기' (push)
        showResult(taskId) {
            return this.loadResult(taskId, 'push');
        },

        /**
         * 실행 결과를 서버(TaskLog)에서 읽어 보인다 — 이 브라우저의 실행 현황과 상관없이 task_id 만으로 열린다.
         * mode: 'push'(사용자가 누름) / 'replace'(완료 자동 표시) / null(URL 에서 읽음, URL 그대로).
         * 결과를 볼 수 없으면(없는 작업·미완료) 알리고 URL 에서 task_id 를 지운다.
         */
        async loadResult(taskId, mode) {
            const seq = ++this.resultSeq;
            this.resultTaskId = taskId;
            this.finalResult = null;
            if (mode) this.setQuery({task_id: taskId}, mode);
            try {
                const res = await this.$axios.get(`/admin/copy-attachments/result/${encodeURIComponent(taskId)}`);
                if (seq !== this.resultSeq) return;
                this.finalResult = normalizeCopyAttachmentsResponse(res.data?.data) || null;
                this.$EventBus.$emit('toast-success', '실행 완료', `복사 완료: ${this.finalResult?.copiedCount || 0}건`);
            } catch (error) {
                if (seq !== this.resultSeq || error?.response?.status === 401) return;
                console.error('Result fetch error:', error);
                const message = getErrorMessage(error, '') || error.message || '결과 조회에 실패했습니다.';
                this.$EventBus.$emit('toast-failure', '결과 조회 실패', message);
                this.resultTaskId = null;
                this.setQuery({task_id: undefined}, 'replace');
            }
        },

        clearResult() {
            this.resultSeq++;
            this.resultTaskId = null;
            this.finalResult = null;
        },

        readStoredTasks() {
            try {
                const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
                return Array.isArray(stored) ? stored.filter(t => t && t.taskId) : [];
            } catch (e) {
                return [];
            }
        },

        loadTrackedTasks() {
            const cutoff = Date.now() - TASK_KEEP_MS;
            this.trackedTasks = this.readStoredTasks()
                .filter(t => !TERMINAL_STATUSES.includes(t.status) || !(Date.parse(t.startedAt) < cutoff));
        },

        /**
         * 여러 탭이 같은 키를 쓴다 — 저장 직전에 다시 읽어 다른 탭의 작업은 남기고 이 탭의 작업만 덮어쓴다.
         * removedIds: 이 탭에서 닫은 작업 (다른 탭 사본에서도 지운다).
         * ponytail: 두 탭이 같은 순간 저장하면 한쪽 추가가 빠질 수 있다. 기기·브라우저를 넘어 보려면 서버의 작업 목록 API(TaskLog 조회)가 필요
         */
        saveTrackedTasks(removedIds = []) {
            try {
                const mine = new Set(this.trackedTasks.map(t => t.taskId));
                const others = this.readStoredTasks().filter(t => !mine.has(t.taskId) && !removedIds.includes(t.taskId));
                const merged = [...others, ...this.trackedTasks]
                    .sort((a, b) => String(a.startedAt || '').localeCompare(String(b.startedAt || '')));
                localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
            } catch (e) {
                // 저장 실패는 이 탭 화면에 영향이 없다
            }
        },

        // 다른 탭이 저장하면 다시 읽는다 (여기서 다시 저장하지 않는다 — 탭끼리 이벤트가 왕복하지 않게)
        onStorage(event) {
            if (event.key && event.key !== STORAGE_KEY) return;
            this.loadTrackedTasks();
            if (this.activeTaskCount > 0 && !this.isPolling) this.startPolling();
        },

        // 서버 작업이 멈춘 채 STARTED 로 남은 행을 치우는 출구 — 로컬 목록에서만 제거한다.
        async stopTracking(item) {
            const ok = await this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: '추적 중지',
                message: `[${item.projectCode}] 작업의 진행 추적을 이 화면에서만 중지합니다. 서버의 복사 작업은 계속되며, 중지 후에는 진행률과 결과를 다시 볼 수 없습니다.`,
            });
            if (ok) this.dismissTrackedTask(item.taskId);
        },

        dismissTrackedTask(taskId) {
            this.trackedTasks = this.trackedTasks.filter(t => t.taskId !== taskId);
            this.saveTrackedTasks([taskId]);
        },

        isTerminalStatus(status) {
            return TERMINAL_STATUSES.includes(status);
        },

        getTaskStatusVariant(status) {
            const map = {
                PENDING: 'secondary',
                SENT: 'secondary',
                STARTED: 'info',
                SUCCESS: 'success',
                FAILURE: 'danger',
                REVOKED: 'warning',
            };
            return map[status] || 'secondary';
        },

        formatTaskTime(dateStr) {
            if (!dateStr) return '-';
            const d = new Date(dateStr);
            const pad = n => String(n).padStart(2, '0');
            return `${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
        },

        downloadPreviewCsv() {
            const rows = this.allPreviewRows;
            if (!rows.length) return;

            const headers = this.previewFields.map(f => f.label);
            const keys = this.previewFields.map(f => f.key);
            const csvRows = [
                headers.join(','),
                ...rows.map(row =>
                    keys.map(key => {
                        const val = String(row[key] ?? '');
                        return `"${val.replace(/"/g, '""')}"`;
                    }).join(',')
                ),
            ];

            const bom = '\uFEFF';
            const blob = new Blob([bom + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `copy_attachments_preview_${this.previewProjectCode || 'unknown'}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        },

        async downloadResultCsv() {
            // 보고 있는 결과의 작업·프로젝트로 받는다 (선택 폼의 프로젝트가 아니라)
            const taskId = this.resultTaskId;
            if (!taskId) return;

            this.csvDownloading = true;
            try {
                const res = await this.$axios.get(
                    `/admin/copy-attachments/result/${encodeURIComponent(taskId)}/csv`,
                    { responseType: 'blob' }
                );

                const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'text/csv' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `copy_attachments_${this.finalResult?.projectCode || taskId}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(link.href);

                this.$EventBus.$emit('toast-success', 'CSV 다운로드', 'CSV 파일 다운로드가 시작되었습니다.');
            } catch (error) {
                if (error?.response?.status === 401) return;
                console.error('CSV download error:', error);
                this.$EventBus.$emit('toast-failure', 'CSV 다운로드 실패', error.message || '다운로드 중 오류가 발생했습니다.');
            } finally {
                this.csvDownloading = false;
            }
        },

        // 폼·미리보기·결과만 비운다. 서버에서 도는 작업의 추적(trackedTasks·폴링)은 행별 '닫기'로만 지운다.
        resetAll() {
            this.selectProject(null);
            this.clearResult();
            this.setQuery({project_id: undefined, task_id: undefined}, 'push');
        },
    },
};
</script>

<style lang="scss" scoped>
#copy-attachments-page {
    .summary-box {
        background-color: #f8f9fa;
        border-radius: 0.25rem;
        padding: 0.75rem;
        height: 100%;
    }
}
</style>
